import { Controller } from 'stimulus';
import Choices from 'choices.js';
import { t } from '../javascript/lib/i18n';

// Choices.js escapes an item label this way before its default removeItemLabelText
// uses it; the helper is internal to the library, so this copy keeps the label identical.
function sanitiseLikeChoices(value) {
  if (typeof value !== 'string') {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object') {
      if ('raw' in value) {
        return sanitiseLikeChoices(value.raw);
      }
      if ('trusted' in value) {
        return value.trusted;
      }
    }

    return value;
  }

  return value
    .replace(/&/g, '&amp;')
    .replace(/>/g, '&gt;')
    .replace(/</g, '&lt;')
    .replace(/'/g, '&#039;')
    .replace(/"/g, '&quot;');
}

// An option opts into an icon with data-custom-properties='{"icon": "<i …></i>"}',
// rendered server-side so its text stays plain for the native select.
function prependIcon(element, choice) {
  const icon = choice.customProperties?.icon;
  if (icon) {
    element.insertAdjacentHTML('afterbegin', `${icon} `);
  }
  return element;
}

const defaultTemplates = Choices.defaults.templates;

export default class extends Controller {
  static targets = ['input'];

  connect() {
    this.setup();
    document.addEventListener('turbo:before-render', this.teardownBeforeMorph);
    document.addEventListener('turbo:render', this.setupAfterRender);
  }

  disconnect() {
    document.removeEventListener(
      'turbo:before-render',
      this.teardownBeforeMorph
    );
    document.removeEventListener('turbo:render', this.setupAfterRender);
    this.teardown();
  }

  // A morph render keeps this element, so connect never runs again. Tear down
  // before it and rebuild on turbo:render, which View#render pairs with every
  // turbo:before-render. turbo:morph is unusable: a same-path redirect
  // follow-up announces a morph render but never morphs.
  teardownBeforeMorph = (event) => {
    if (event.detail?.renderMethod !== 'morph') return;

    this.teardown();
  };

  setupAfterRender = (event) => {
    if (event.detail?.renderMethod !== 'morph') return;

    if (this.element.isConnected && this.hasInputTarget && !this.select) {
      this.setup();
    }
  };

  setup() {
    const placeholder = this.inputTarget.getAttribute('placeholder') || '';
    const hasPlaceholder = placeholder !== '' && !this.inputTarget.multiple;
    const isMultiple = this.inputTarget.multiple;

    this.select = new Choices(this.inputTarget, {
      allowHTML: true,
      searchEnabled: true,
      searchChoices: true,
      searchFloor: 2,
      searchPlaceholderValue: t('js.select.type_to_search'),
      searchResultLimit: 50,
      // Tighten fuzzy matching so weak single-character matches are excluded.
      fuseOptions: {
        threshold: 0.2,
        ignoreLocation: true,
      },
      shouldSort: false,
      removeItemButton: isMultiple,
      itemSelectText: '',
      loadingText: t('js.select.loading'),
      noResultsText: t('js.select.no_results'),
      noChoicesText: t('js.select.no_choices'),
      removeItemIconText: () => t('js.select.remove_item'),
      removeItemLabelText: (value, _valueRaw, item) =>
        t('js.select.remove_item_label', {
          label: item ? sanitiseLikeChoices(item.label) : value,
        }),
      placeholder: hasPlaceholder,
      placeholderValue: placeholder,
      classNames: {
        containerOuter: ['choices', 'amba-choices-container'],
        containerInner: ['amba-choices', 'form-select'],
        input: ['amba-choices-input'],
        inputCloned: ['amba-choices-input', 'amba-choices-input--cloned'],
        listItems: ['choices__list--multiple', 'amba-choices-multiple'],
        listDropdown: ['choices__list--dropdown', 'amba-choices-dropdown'],
        list: ['choices__list', 'amba-choices-list'],
        item: ['choices__item', 'amba-choices-item'],
        itemChoice: ['choices__item--choice', 'amba-choices-item--choice'],
        group: ['choices__group', 'amba-choices-group'],
        groupHeading: ['choices__heading', 'amba-choices-group-heading'],
        placeholder: ['choices__placeholder', 'amba-choices-item--placeholder'],
        highlightedState: [
          'is-highlighted',
          'amba-choices-item--is-highlighted',
        ],
        selectedState: ['is-selected', 'amba-choices-item--is-selected'],
        button: ['choices__button', 'amba-choices-button'],
        noResults: ['choices__notice', 'amba-choices-no-results'],
        noChoices: ['choices__notice', 'amba-choices-no-choices'],
      },
      callbackOnCreateTemplates: () => ({
        item(options, choice, ...rest) {
          return prependIcon(
            defaultTemplates.item.call(this, options, choice, ...rest),
            choice
          );
        },
        choice(options, choice, ...rest) {
          return prependIcon(
            defaultTemplates.choice.call(this, options, choice, ...rest),
            choice
          );
        },
      }),
    });

    // Android soft keyboards (GBoard swipe/autocomplete) dispatch keydown with event.key undefined.
    // Registered on the wrapper (this.element) so capture fires before Choices.js's own capture listener.
    this._keydownGuard = (event) => {
      if (event.key === undefined) event.stopImmediatePropagation();
    };
    this.element.addEventListener('keydown', this._keydownGuard, true);

    // The select outlives a morph rebuild, so its listeners go with each Choices instance.
    this.selectListeners = new AbortController();

    const setDropdownZIndex = () => {
      const dropdownList = this.select?.dropdown.element;
      if (dropdownList) {
        dropdownList.style.zIndex = '9999';
      }
    };

    this.select.passedElement.element.addEventListener(
      'showDropdown',
      () => {
        setDropdownZIndex();
        this.scrollDropdownIntoView();
      },
      { signal: this.selectListeners.signal }
    );

    setTimeout(setDropdownZIndex, 0);

    // Move search input to dropdown for multi-select (like single-select behavior)
    if (isMultiple) {
      this.setupDropdownSearchForMultiple();
    }

    this.setupDisabledAttributeObserver();
  }

  setupDropdownSearchForMultiple() {
    const dropdown = this.select.dropdown.element;
    const input = this.select.input.element;

    // Add class for styling before moving (prevents flash)
    dropdown.classList.add('amba-choices-dropdown--with-search');

    // Set placeholder like single-select does
    this.select.input.placeholder =
      this.select.config.searchPlaceholderValue ||
      t('js.select.type_to_search');

    // Move input from inline position to top of dropdown
    dropdown.insertBefore(input, dropdown.firstChild);

    // Ensure dropdown is closed after setup (moving input can trigger focus)
    this.select.hideDropdown();
    this.select.input.blur();

    const selectElement = this.select.passedElement.element;
    const { signal } = this.selectListeners;

    // Focus search input when dropdown opens
    selectElement.addEventListener(
      'showDropdown',
      () => this.select.input.focus(),
      { signal }
    );

    // Clear search and reset filter when dropdown closes
    selectElement.addEventListener(
      'hideDropdown',
      () => this.clearSearchInput(),
      { signal }
    );

    // Also clear when an item is selected (in case dropdown stays open)
    selectElement.addEventListener('choice', () => this.clearSearchInput(), {
      signal,
    });
  }

  clearSearchInput() {
    const input = this.select.input;
    if (input.value) {
      input.element.value = '';
      // Dispatch input event to trigger Choices.js search reset
      input.element.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  teardown() {
    if (this._keydownGuard) {
      this.element.removeEventListener('keydown', this._keydownGuard, true);
    }

    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.fieldsetObserver) {
      this.fieldsetObserver.disconnect();
    }

    this.selectListeners?.abort();

    if (this.select?.initialised) {
      this.select.destroy();
    }
    this.select = undefined;
  }

  scrollDropdownIntoView() {
    this.select.dropdown.element.scrollIntoView({ block: 'nearest' });
  }

  setupDisabledAttributeObserver() {
    // If the original select element has it's disabled state changed,
    // update this for the choices element
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'disabled'
        ) {
          const isDisabled = this.inputTarget.hasAttribute('disabled');

          if (isDisabled) {
            this.select.disable();
          } else {
            this.select.enable();
          }
        }
      });
    });

    this.observer.observe(this.inputTarget, {
      attributes: true,
      attributeFilter: ['disabled'],
    });

    this.setupFieldsetObserver();
  }

  // A disabled ancestor fieldset disables the select without touching its own
  // `disabled` attribute, so the observer above never sees it — and Choices, which
  // holds the interaction in its own markup, would stay live inside a section the
  // form cannot submit. Watch the fieldsets around it too, with its own record of
  // what they said last: `Choices#disable` mirrors the state onto the select, so
  // the select's attribute cannot answer whether the fieldset is still closed.
  setupFieldsetObserver() {
    const fieldsets = this.ancestorFieldsets;
    if (fieldsets.length === 0) {
      return;
    }

    this.disabledByFieldset = false;
    this.fieldsetObserver = new MutationObserver(() =>
      this.syncFieldsetState()
    );
    fieldsets.forEach((fieldset) =>
      this.fieldsetObserver.observe(fieldset, {
        attributes: true,
        attributeFilter: ['disabled'],
      })
    );

    this.syncFieldsetState();
  }

  syncFieldsetState() {
    const disabled = this.inputTarget.closest('fieldset:disabled') !== null;
    if (disabled === this.disabledByFieldset) {
      return;
    }

    this.disabledByFieldset = disabled;

    if (disabled) {
      this.select.disable();
    } else {
      this.select.enable();
    }
  }

  get ancestorFieldsets() {
    const fieldsets = [];
    let fieldset = this.inputTarget.closest('fieldset');

    while (fieldset) {
      fieldsets.push(fieldset);
      fieldset = fieldset.parentElement
        ? fieldset.parentElement.closest('fieldset')
        : null;
    }

    return fieldsets;
  }
}
